import Tooltip from "../ui-control/Tooltip";
import DemandStudioPreview from "./DemandStudioPreview";
import DemandStudioPreviewSelectors from "./DemandStudioPreviewSelectors";
import DemandStudioSkeleton from "./DemandStudioSkeleton";
import DemandStudioEditor from "./DemandStudioEditor";
import useDemandStudioLayout from "../../hooks/useDemandStudioLayout";
import { DemandStudioProvider } from "../../contexts/DemandStudioContext";
import DemandStudioToolBar from "./DemandStudioToolBar";
import DemandStudioExitOverlay from "./DemandStudioExitOverlay";
import { Link } from "react-router-dom";
import useDemandStudioPreview from "../../hooks/useDemandStudioPreview";
import useCompanyTier from "../../hooks/useCompanyTier";
import RenderIf from "../render-if/Renderif";
import DemandStudioTour from "../tour/DemandStudioTour";
import { TOUR_TARGETS } from "../../constants/tourTargets";
import { useProductContext } from "../../contexts/ProductContext";

const fullscreenClasses =
  "flex flex-col h-[calc(100vh-56px)] overflow-hidden";

const headerClasses =
  "flex flex-col gap-2 py-6 px-10 border-b border-b-border " +
  "shrink-0 bg-white shadow-header";

const headerRowClasses = "flex items-center justify-between w-full";

const headerLeftClasses = "flex flex-1 items-center gap-4 min-w-0";

const headerTitleClasses =
  "text-section-title text-content font-serif " +
  "m-0! max-w-[50vw] whitespace-nowrap overflow-hidden text-ellipsis";

const viewToggleBtnBase =
  "inline-flex items-center gap-1.5 py-1.5 px-3 text-xs font-semibold " +
  "font-secondary text-subtle bg-transparent border border-border rounded-md " +
  "cursor-pointer transition-all duration-150 " +
  "hover:text-primary hover:border-primary hover:bg-primary/[0.05] " +
  "[&_i]:text-[13px]";

const viewToggleBtnActive =
  "text-primary! border-primary! bg-primary/[0.08]!";

const bodyClasses = "flex-1 overflow-hidden p-6 bg-surface-card min-h-0";

const previewContainerClasses = "h-full";

const lifecarePlanCtaClasses =
  "inline-flex items-center gap-2 py-1.5 px-3.5 text-xs font-semibold " +
  "font-secondary rounded-md cursor-pointer transition-all duration-150 " +
  "text-white! border border-transparent " +
  "bg-primary " +
  "shadow-brand-cta hover:-translate-y-px hover:shadow-brand-cta-hover";

const DemandStudioLayout = ({
  useStudioState,
  previewSelectorsInHeader = false,
}) => {
  const {
    studioState,
    shouldShowHeaderSelectors,
    shouldHideBodySelectors,
  } = useDemandStudioLayout({ useStudioState, previewSelectorsInHeader });

  const {
    caseName,
    isInitialLoadPending,
    showPreview,
    isTabView,
    toggleViewMode,
    caseId,
    caseDetails,
    clients,
    isFinalizingExit,
    regenerate,
    suggestedEditsSettledSignal
  } = studioState;

  const previewState = useDemandStudioPreview({
    caseId,
    isOpen: showPreview,
    docxFiles: caseDetails?.docxFiles,
    clients,
    onStaleDocuments: regenerate,
    suggestedEditsSettledSignal
  });

  const { isDiamondTier } = useCompanyTier();
  // Products embedding the engine can opt out of the preliminary lifecare flow;
  // standalone keeps it, so absence means enabled.
  const { hasPreliminaryLifecarePlan } = useProductContext();
  const showLifecarePlanCta = isDiamondTier && (hasPreliminaryLifecarePlan ?? true);

  return (
    <DemandStudioProvider value={studioState}>
      <div className={fullscreenClasses}>
        <div className={headerClasses}>
          <div className={headerRowClasses}>
            <div className={headerLeftClasses}>
              <h1 className={headerTitleClasses}>{caseName}</h1>
              <Tooltip
                title={isTabView ? "Switch to scroll view" : "Switch to tab view"}
                placement="bottom"
                leaveDelay={50}
              >
                <button
                  type="button"
                  data-tour={TOUR_TARGETS.DEMAND_STUDIO_VIEW_TOGGLE}
                  className={`${viewToggleBtnBase} ${isTabView ? viewToggleBtnActive : ""}`}
                  onClick={toggleViewMode}
                  aria-label={isTabView ? "Switch to scroll view" : "Switch to tab view"}
                >
                  <i className={isTabView ? "fa fa-list" : "fa fa-columns"} />
                  <span>{isTabView ? "Scroll View" : "Tab View"}</span>
                </button>
              </Tooltip>
              {shouldShowHeaderSelectors && (
                <DemandStudioPreviewSelectors previewState={previewState} />
              )}
            </div>
            <div className="flex items-center gap-3">
              <RenderIf shouldRender={showLifecarePlanCta}>
                <Link to={`/case/${caseId}/preliminary-lifecare-plan`} className={lifecarePlanCtaClasses}>
                  <i className="fas fa-wand-magic-sparkles" />
                  <span>Preliminary Life Care Plan</span>
                </Link>
              </RenderIf>
              <DemandStudioToolBar />
            </div>
          </div>
        </div>

        <div className={bodyClasses}>
          {isInitialLoadPending ? (
            <DemandStudioSkeleton />
          ) : showPreview ? (
            <div className={previewContainerClasses}>
              <DemandStudioPreview
                previewState={previewState}
                hideSelectors={shouldHideBodySelectors}
              />
            </div>
          ) : (
            <DemandStudioEditor />
          )}
        </div>
      </div>
      <DemandStudioExitOverlay isVisible={isFinalizingExit} />
      <DemandStudioTour />
    </DemandStudioProvider>
  );
};

export default DemandStudioLayout;
